import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { TextFieldTheme } from "@wix/wix-ui-tpa/cssVars";
import { useState, useMemo, useCallback } from "react";
import { useConfig } from "../../contexts/config-context.js";
import { useAddressAutofill } from "../../fields/multiline-address/multiline-address/address-autofill-context.js";
import { useCountryCode } from "../../fields/multiline-address/multiline-address/country-code-context.js";
import { useFieldAttributes } from "../../services/field-attributes.js";
import { createOption, AutocompleteWUT } from "../autocomplete/autocomplete-wut.js";
import { FieldDescriptionWUT } from "../field-description/field-description-wut.js";
import { FieldErrorWUT } from "../field-error/field-error-wut.js";
import { FieldLabelWUT } from "../field-label/field-label-wut.js";
import { useAtlas } from "./use-atlas.js";
import usePlacesAutocomplete from "./use-places-autocomplete.js";
const allowAllOptions = () => true;
const AddressInputWUT = ({
  id,
  target,
  label,
  hideLabel,
  placeholder,
  required,
  description,
  value,
  onChange,
  error,
  hasError = false,
  errorMessage,
  disabled,
  onBlur,
  onFocus,
  FieldLayout,
  requiredIndicatorType,
  requiredIndicatorPlacement
}) => {
  const { labelId, inputId, errorId, descriptionId, ariaDescribedBy } = useFieldAttributes(id, error, description);
  const { instance, language, atlasBaseUrl } = useConfig();
  const { onAddressSelect } = useAddressAutofill();
  const countryCode = useCountryCode();
  const [isAtlasEnabled, setIsAtlasEnabled] = useState(false);
  const atlas = useAtlas({
    enabled: isAtlasEnabled,
    getInstance: () => instance ?? "",
    language,
    atlasBaseUrl
  });
  const { predictions, updatePredictions, clearPredictions } = usePlacesAutocomplete({ atlas });
  const options = useMemo(
    () => predictions.map((prediction) => {
      return createOption({
        id: prediction.searchId,
        value: prediction.description
      });
    }),
    [predictions]
  );
  const handleChange = useCallback(
    (event) => {
      const newValue = event.target.value;
      const countryCodes = countryCode ? [countryCode] : void 0;
      updatePredictions({ input: newValue, countryCodes });
      onChange(newValue);
    },
    [onChange, updatePredictions, countryCode]
  );
  const handleSelect = useCallback(
    async (option) => {
      clearPredictions();
      if (!atlas) {
        return;
      }
      const address = await atlas.getPlace({ searchId: option.id });
      if (address && onAddressSelect) {
        onAddressSelect(address);
      }
    },
    [onAddressSelect, clearPredictions, atlas]
  );
  const handleManualInput = useCallback(() => {
    clearPredictions();
  }, [clearPredictions]);
  const handleBlur = useCallback(
    (event) => {
      const newValue = event.target.value;
      clearPredictions();
      onChange(newValue);
      onBlur?.();
    },
    [onChange, onBlur, clearPredictions]
  );
  const handleFocus = useCallback(() => {
    setIsAtlasEnabled(true);
    onFocus?.();
  }, [onFocus]);
  return /* @__PURE__ */ jsx(
    FieldLayout,
    {
      fieldId: id,
      renderLabel: () => /* @__PURE__ */ jsx(
        FieldLabelWUT,
        {
          labelId,
          htmlFor: inputId,
          required,
          label,
          hideLabel,
          requiredIndicatorType,
          requiredIndicatorPlacement
        }
      ),
      renderInput: () => /* @__PURE__ */ jsx(
        AutocompleteWUT,
        {
          id: inputId,
          value,
          options,
          disabled,
          placeholder,
          error: hasError,
          filterPredicate: allowAllOptions,
          onChange: handleChange,
          onSelect: handleSelect,
          onManualInput: handleManualInput,
          onBlur: handleBlur,
          onFocus: handleFocus,
          ariaDescribedBy,
          theme: TextFieldTheme.Box
        }
      ),
      renderDescription: () => /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx(
          FieldErrorWUT,
          {
            target,
            hasError,
            errorMessage,
            id: errorId
          }
        ),
        /* @__PURE__ */ jsx(FieldDescriptionWUT, { description, id: descriptionId })
      ] })
    }
  );
};
export {
  AddressInputWUT
};
//# sourceMappingURL=address-input-wut.js.map
